import "./DynamicModelOverview.scss";
import React from "react";
import ChartContainer from "../ChartContainer";
import { InfoTooltip } from "../InfoTooltip";
import { Spinner } from "../Spinner";

export type ModelOverviewRow = {
  label: string | React.ReactNode;
  value: string | React.ReactNode;
  tooltipText?: string;
};

export type ModelOverviewSection = {
  title: string;
  rows: ModelOverviewRow[];
  subsections?: ModelOverviewRow[][];
};

export interface ModelOverviewProps {
  title?: string;
  sections: ModelOverviewSection[];
  rightActions?: React.ReactNode;
  height?: number;
  loading?: boolean;
}

const HEADER_HEIGHT = 68;
const CHART_BODY_PADDING = 48;

const DataRow = ({ label, value, tooltipText }) => (
  <div className="dataRow">
    <div className="label">
      <span>{label}:</span>
      {tooltipText && <InfoTooltip size="reg">{tooltipText}</InfoTooltip>}
    </div>
    <div className="value">{value}</div>
  </div>
);

const DynamicModelOverview = ({
  title = "Model Overview",
  sections,
  rightActions,
  height,
  loading = false,
}: ModelOverviewProps): JSX.Element => (
  <ChartContainer
    enableHoverDesign
    title={title}
    rightActions={rightActions}
    height={height}
    className="dynamicModelOverview"
  >
    {loading ? (
      <div style={{ height: height ? height - HEADER_HEIGHT - CHART_BODY_PADDING : undefined }}>
        <Spinner />
      </div>
    ) : (
      <div className="chartBody">
        {sections.map(({ title: sectionTitle, rows, subsections = [] }, sectionIndex) => (
          <React.Fragment key={`section-${sectionIndex}`}>
            <div className="dataSection">
              {sectionTitle && <div className="dataSectionTitle">{sectionTitle}</div>}
              <div className="dataBody">
                {rows.map(({ label, value, tooltipText }, rowIndex) => (
                  <DataRow
                    key={`section-${sectionIndex}-row-${rowIndex}`}
                    label={label}
                    value={value}
                    tooltipText={tooltipText}
                  />
                ))}
              </div>
              {subsections.map((subsection, subsectionIndex) => (
                <React.Fragment key={`section-${sectionIndex}-subsection-${subsectionIndex}`}>
                  <hr className="divider subsectionDivider" />
                  <div className="dataSubsection">
                    {subsection.map(({ label, value, tooltipText }, rowIndex) => (
                      <DataRow
                        key={`section-${sectionIndex}-subsection-${subsectionIndex}-row-${rowIndex}`}
                        label={label}
                        value={value}
                        tooltipText={tooltipText}
                      />
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </div>
            {sectionIndex < sections.length - 1 && <hr className="divider sectionDivider" />}
          </React.Fragment>
        ))}
      </div>
    )}
  </ChartContainer>
);

export default DynamicModelOverview;
